import { Box, Card, CardContent, Divider, Grid, IconButton, Typography, useMediaQuery } from '@mui/material'

import { FC } from 'react'
import {
  getColorForAnimalVaccineStatus,
  getReadableAnimalBreedTitle,
  getReadableAnimalFieldLabel,
  getReadableAnimalHealthPathology,
  getReadableAnimalVaccineStatus,
  getReadableIdentificationNumber,
  getReadableTattooLocation,
} from '../../../domain/Animal/AnimalDisplay'
import { AnimalDto } from '../../../interactors/gen/backendClient'

import { ChevronRightRounded } from '@mui/icons-material'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useCanEditAnimal } from '../../../store/useCanEditAnimal'
import { ReadOnlyChip } from '../../common/ReadOnlyChip'
import { ReadOnlyItem } from '../../common/ReadOnlyItem'
import { surfaceCardSx } from '../../common/surfaceCardSx'
import { theme } from '../../theme'
import { EditGeneralInfoAnimalPrompt } from './EditGeneralInfoAnimalPrompt'

interface Props {
  animal: AnimalDto
}

const EMPTY_VALUE = '-'

const ReadOnlyInlineItem: FC<{ label: string; value: string; allowWrap?: boolean }> = ({
  label,
  value,
  allowWrap = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 1,
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: '#7A8197',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.01em',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 400,
        color: '#2B4166',
        fontSize: 12.5,
        lineHeight: 1.2,
        whiteSpace: allowWrap ? 'normal' : 'nowrap',
        overflow: allowWrap ? 'visible' : 'hidden',
        textOverflow: allowWrap ? 'clip' : 'ellipsis',
        wordBreak: allowWrap ? 'break-word' : 'normal',
        textAlign: 'right',
        minWidth: 0,
      }}
      title={value}
    >
      {value || EMPTY_VALUE}
    </Typography>
  </Box>
)

export const AnimalGeneralCard: FC<Props> = ({ animal }) => {
  const canEditAnimal = useCanEditAnimal()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const hasIdentificationNumber =
    animal.identificationNumber !== null &&
    animal.identificationNumber !== undefined &&
    animal.identificationNumber !== ''

  const needsToConfirmDeworming =
    animal.health?.operations.find(
      (operation) =>
        operation.type.includes('deworming') &&
        dayjs(operation.date, 'DD/MM/YYYY').isBefore(dayjs()) &&
        !operation.validated
    )?.needsValidation === true
      ? 'RDV à valider'
      : undefined

  const nextDewormingDate =
    animal.health?.operations.find(
      (operation) => operation.type.includes('deworming') && dayjs(operation.date, 'DD/MM/YYYY').isAfter(dayjs())
    )?.date ?? needsToConfirmDeworming

  const needsToConfirmFleaControl =
    animal.health?.operations.find(
      (operation) =>
        operation.type.includes('flea-control') &&
        dayjs(operation.date, 'DD/MM/YYYY').isBefore(dayjs()) &&
        !operation.validated
    )?.needsValidation === true
      ? 'RDV à valider'
      : undefined

  const nextFleaControlDate =
    animal.health?.operations.find(
      (operation) => operation.type.includes('flea-control') && dayjs(operation.date, 'DD/MM/YYYY').isAfter(dayjs())
    )?.date ?? needsToConfirmFleaControl

  const birthdayDisplay = animal.birthday?.date
    ? `${dayjs(animal.birthday?.date).format('DD/MM/YYYY')}${animal.birthday?.isApproximate ? ' (approx...)' : ''}`
    : EMPTY_VALUE

  const breedDisplay = animal.breedData?.breed || animal.breed || EMPTY_VALUE

  const identificationDisplay = animal.identificationNumber
    ? getReadableIdentificationNumber(animal.identificationNumber?.toString())
    : EMPTY_VALUE

  const tattooNumber = animal?.tattoo?.number
  const tattooLocation = animal?.tattoo?.location ? getReadableTattooLocation(animal?.tattoo?.location) : undefined
  const tattooDisplay =
    tattooNumber || tattooLocation
      ? `${tattooNumber || EMPTY_VALUE}${tattooLocation ? ` - ${tattooLocation}` : ''}`
      : EMPTY_VALUE

  const hasTattooData = Boolean(tattooNumber || tattooLocation)
  const identificationSlotLabel = hasIdentificationNumber
    ? getReadableAnimalFieldLabel('identificationNumber')
    : hasTattooData
    ? getReadableAnimalFieldLabel('tattoo')
    : getReadableAnimalFieldLabel('identificationNumber')
  const identificationSlotValue = hasIdentificationNumber
    ? identificationDisplay
    : hasTattooData
    ? tattooDisplay
    : EMPTY_VALUE

  const vaccinationDisplay = animal.vaccination.vaccinationStatus
    ? getReadableAnimalVaccineStatus(animal.vaccination.vaccinationStatus)
    : EMPTY_VALUE

  const nextDewormingDisplay = nextDewormingDate ?? EMPTY_VALUE
  const nextFleaControlDisplay = nextFleaControlDate ?? EMPTY_VALUE

  return (
    <>
      <Card
        sx={{
          ...surfaceCardSx,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent
          sx={{
            pb: { xs: '16px !important', sm: '16px !important', md: '10px !important' },
            pt: { xs: 2, md: 2.25 },
            px: { xs: 2, md: 2.25 },
            flex: '1 1 auto',
          }}
        >
          <Box sx={{ display: 'flex', width: '100%', mb: 1, alignItems: 'stretch' }}>
            <Box sx={{ width: '100%', pr: 1.5, display: 'flex', alignItems: 'center' }}>
              {isMobile ? (
                <Grid container spacing={1.1}>
                  <Grid item xs={12}>
                    <ReadOnlyInlineItem label="Date de naissance" value={birthdayDisplay} />
                  </Grid>

                  <Grid item xs={12}>
                    <ReadOnlyInlineItem
                      label={
                        animal.breedData?.breedTitle
                          ? getReadableAnimalBreedTitle(animal.breedData?.breedTitle)
                          : 'Type de race'
                      }
                      value={breedDisplay}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ReadOnlyInlineItem label={identificationSlotLabel} value={identificationSlotValue} allowWrap />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={1.25} alignItems="start">
                  <Grid item xs={6} sm={3} marginBottom={1.25}>
                    <ReadOnlyItem label="Date de naissance" value={birthdayDisplay} />
                  </Grid>

                  <Grid item xs={6} sm={3} marginBottom={1.25}>
                    <ReadOnlyItem
                      label={
                        animal.breedData?.breedTitle
                          ? getReadableAnimalBreedTitle(animal.breedData?.breedTitle)
                          : 'Type de race'
                      }
                      value={breedDisplay}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} marginBottom={1.25}>
                    <ReadOnlyItem label={identificationSlotLabel} value={identificationSlotValue} multiline />
                  </Grid>
                </Grid>
              )}
            </Box>

            {canEditAnimal && (
              <Box
                sx={{
                  width: '46px',
                  borderLeft: '1px solid rgba(122, 129, 151, 0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <EditGeneralInfoAnimalPrompt animal={animal} />
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', width: '100%', mt: 1, alignItems: 'stretch' }}>
            <Box sx={{ width: '100%', pr: 1.5, display: 'flex', alignItems: 'center' }}>
              <Grid container spacing={1.25} alignItems="start">
                {isMobile ? (
                  <>
                    <Grid item xs={12}>
                      <ReadOnlyInlineItem label="Prochain vermifuge" value={nextDewormingDisplay} />
                    </Grid>

                    <Grid item xs={12}>
                      <ReadOnlyInlineItem label="Prochain antiparasitaire" value={nextFleaControlDisplay} />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={6} sm={3} marginBottom={1.25}>
                      <ReadOnlyChip
                        label={`Statut vaccinal`}
                        value={vaccinationDisplay}
                        chipColor={
                          animal.vaccination.vaccinationStatus
                            ? getColorForAnimalVaccineStatus(animal.vaccination.vaccinationStatus)
                            : undefined
                        }
                      />
                    </Grid>

                    {animal.health.pathologies && animal.health.pathologies.length > 0 && (
                      <Grid item xs={6} sm={3} marginBottom={1.25}>
                        <ReadOnlyChip
                          label="Pathologie"
                          value={`${getReadableAnimalHealthPathology(animal.health.pathologies[0])}${
                            animal.health.pathologies.length > 1 ? ` +${animal.health.pathologies.length - 1}` : ''
                          }`}
                          chipColor="#E4E8F2"
                        />
                      </Grid>
                    )}

                    <Grid item xs={6} sm={3} marginBottom={1.25}>
                      <ReadOnlyItem label={`Prochain vermifuge`} value={nextDewormingDisplay} />
                    </Grid>

                    <Grid item xs={6} sm={3} marginBottom={1.25}>
                      <ReadOnlyItem label="Prochain antiparasitaire" value={nextFleaControlDisplay} />
                    </Grid>
                  </>
                )}

                {/* Pathologies chips handled in mobile (bottom row) and desktop (inline chip) */}
              </Grid>
            </Box>

            {canEditAnimal && (
              <Box
                sx={{
                  width: '46px',
                  borderLeft: '1px solid rgba(122, 129, 151, 0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconButton
                  onClick={() => {
                    navigate('./?tab=sante')
                    const chatContainer = document.querySelector('.scrollable-content') as HTMLElement
                    chatContainer.scrollTop = 600
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    color: '#203864',
                    backgroundColor: 'rgba(32, 56, 100, 0.08)',
                    '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
                  }}
                >
                  <ChevronRightRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  )
}
